import { BufferWriter, Writer } from "protobufjs";
import { Customer } from "src/domain/model/customer";
import { JobCareer } from "src/domain/model/job_career";
import { PastCaseProject } from "src/domain/model/past_case_project";
import { OccupationSkillTag } from "src/domain/model/skill_tag";
import { TJobCareerWithCustomerID } from "src/domain/repository/job_career_repository";
import { TOccupationGetAllRequest } from "src/domain/repository/skill_tag_repository";
import {
  CustomerConvertResponse,
  CustomerConvertResponseList,
} from "src/infra/api/service/customer_api_convert";
import {
  JobCareerConvertResponse,
  JobCareerConvertResponseListByCustomerIDs,
} from "src/infra/api/service/job_career_api_convert";
import {
  OccupationSkillTagConvertResponse,
  OccupationSkillTagConvertResponseList,
} from "src/infra/api/service/skill_tag_api_convert";
import { IApiClient } from "../client";
import { common, entity } from "../rpc/api";

const emptyToken = (): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    resolve("");
  });
};

class PublicAPI {
  constructor(readonly apiClient: IApiClient) {}

  public getCustomer(customerID: string): Promise<Customer> {
    return new Promise<Customer>((resolve, reject) => {
      emptyToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.CustomerID = new entity.CustomerID();
          req.id = customerID;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PublicService/GetCustomer",
            token,
            entity.CustomerID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.SafeCustomer = entity.SafeCustomer.decode(binary);
          const resConverted: CustomerConvertResponse =
            CustomerConvertResponse.fromSafe(res);
          const item: Customer = Customer.from(resConverted);
          resolve(item);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getRecommmendCustomerByMatch(id: string): Promise<Customer[]> {
    return new Promise<Customer[]>((resolve, reject) => {
      emptyToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.CustomerID = new entity.CustomerID();
          req.id = id;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PublicService/GetRecommendCustomerByMatch",
            token,
            entity.CustomerID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.SafeCustomerList =
            entity.SafeCustomerList.decode(binary);
          const resConverted: CustomerConvertResponseList =
            CustomerConvertResponseList.from(res);
          const items: Customer[] = resConverted.items.map(
            (item: CustomerConvertResponse): Customer => {
              return Customer.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getRecommmendCustomerByTrend(id: string): Promise<Customer[]> {
    return new Promise<Customer[]>((resolve, reject) => {
      emptyToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.CustomerID = new entity.CustomerID();
          req.id = id;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PublicService/GetRecommendCustomerByTrend",
            token,
            entity.CustomerID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.SafeCustomerList =
            entity.SafeCustomerList.decode(binary);
          const resConverted: CustomerConvertResponseList =
            CustomerConvertResponseList.from(res);
          const items: Customer[] = resConverted.items.map(
            (item: CustomerConvertResponse): Customer => {
              return Customer.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllJobCareerByCustomerIDs(
    ids: string[]
  ): Promise<TJobCareerWithCustomerID[]> {
    return new Promise<TJobCareerWithCustomerID[]>((resolve, reject) => {
      emptyToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.CustomerIDList = new entity.CustomerIDList();
          req.ids = ids;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PublicService/GetAllJobCareerByCustomerIDs",
            token,
            entity.CustomerIDList.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.JobCareerWithCustomerIDList =
            entity.JobCareerWithCustomerIDList.decode(binary);
          const items: TJobCareerWithCustomerID[] = res.items.map(
            (item: entity.JobCareerWithCustomerID) => {
              const resConverted: JobCareerConvertResponseListByCustomerIDs =
                JobCareerConvertResponseListByCustomerIDs.from(item);
              const resJobCareers: JobCareer[] = resConverted.items.map(
                (convertItem: JobCareerConvertResponse): JobCareer => {
                  return JobCareer.from(convertItem);
                }
              );
              return {
                jobCareers: resJobCareers,
                customerID: item.customerID,
              };
            }
          );

          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllPastCaseProject(customerId: string): Promise<PastCaseProject[]> {
    return new Promise<PastCaseProject[]>((resolve, reject) => {
      emptyToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.CustomerID = new entity.CustomerID();
          req.id = customerId;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PublicService/GetAllPastCaseProject",
            token,
            entity.CustomerID.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.PastCaseProjectList =
            entity.PastCaseProjectList.decode(binary);
          resolve(
            res.items.map((item: entity.PastCaseProject): PastCaseProject => {
              return PastCaseProject.from(item);
            })
          );
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  }

  public getAllOccupationSkillTag = (
    occupationGetAllRequest: TOccupationGetAllRequest
  ): Promise<OccupationSkillTag[]> => {
    return new Promise<OccupationSkillTag[]>((resolve, reject) => {
      emptyToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.OccupationSkillTagIDWithPager =
            new entity.OccupationSkillTagIDWithPager();
          req.id = occupationGetAllRequest.id;
          req.pager = occupationGetAllRequest.pager;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PublicService/GetAllOccupationSkillTag",
            token,
            entity.OccupationSkillTagIDWithPager.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.OccupationSkillTagList =
            entity.OccupationSkillTagList.decode(binary);
          const resConverted: OccupationSkillTagConvertResponseList =
            OccupationSkillTagConvertResponseList.from(res);
          const items: OccupationSkillTag[] = resConverted.items.map(
            (item: OccupationSkillTagConvertResponse): OccupationSkillTag => {
              return OccupationSkillTag.from(item);
            }
          );
          resolve(items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };

  public getAllReview = (
    customerId: string,
    page: number,
    offset: number
  ): Promise<entity.IReview[]> => {
    return new Promise<entity.IReview[]>((resolve, reject) => {
      emptyToken()
        .then((token: string): Promise<Uint8Array> => {
          const req: entity.CustomerIDWithPager =
            new entity.CustomerIDWithPager();
          const pager = new common.Pager();
          pager.page = page;
          pager.offset = offset;
          req.id = customerId;
          req.pager = pager;
          const writer: BufferWriter | Writer = Writer.create();
          return this.apiClient.postWithToken(
            "/api.PublicService/GetAllReviewByCustomer",
            token,
            entity.CustomerIDWithPager.encode(req, writer).finish()
          );
        })
        .then((binary: Uint8Array): void => {
          const res: entity.ReviewList = entity.ReviewList.decode(binary);
          resolve(res.items);
        })
        .catch((error: Error): void => {
          reject(error);
        });
    });
  };
}

const createPublicAPI = (apiClient: IApiClient): PublicAPI => {
  return new PublicAPI(apiClient);
};

export { createPublicAPI };



// WEBPACK FOOTER //
// ./src/infra/api/service/public_api.ts